import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Step0DeliveryType from './Step0DeliveryType';
import Step1Products from './Step1Products';
import Step2Addons from './Step2Addons';
import Step2Delivery from './Step2Delivery';
import Step2MeetUp from './Step2MeetUp';
import Step3Customer from './Step3Customer';
import Step4Review from './Step4Review';
import CorporateContactScreen from './CorporateContactScreen';
import CateringInquiryForm from './CateringInquiryForm';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const STORAGE_KEY = 'phoenix_kabob_cart';

const DEFAULT_ORDER_RULES = {
  meal_prep_delivery_days : [1],
  meal_prep_cutoff_day    : 5,
  meal_prep_cutoff_time   : '23:59',
  meal_prep_min_qty       : 5,
  meal_prep_max_qty       : 20,
  meal_prep_daily_limit   : 10,
  corporate_delivery_days : [1, 2, 3, 4, 5],
  corporate_cutoff_day    : 4,
  corporate_cutoff_time   : '17:00',
  meet_up_min_qty         : 5,
  meet_up_discount_pct    : 0,
};

const DISPLAY_STEPS = [
  { label: 'Order Type', display: 1, stepKey: '0' },
  { label: 'Menu',       display: 2, stepKey: 1 },
  { label: 'Add-ons',    display: 3, stepKey: 'addons' },
  { label: 'Details',    display: 4, stepKey: 2 },
  { label: 'Review',     display: 5, stepKey: 4 },
];

function getDisplayStep(step) {
  if (step === '0')      return 1;
  if (step === 1)        return 2;
  if (step === 'addons') return 3;
  if (step === 2)        return 4;
  return 5;
}

// Steps that bypass the normal form flow
const SPECIAL_STEPS = new Set(['corporate_contact', 'catering']);

export default function MultiStepForm({ primaryColor, corporateClient, session: externalSession }) {
  const navigate = useNavigate();
  const [step, setStep]               = useState(corporateClient ? 1 : '0');
  const [formData, setFormData]       = useState({ addons: [] });
  const [session, setSession]         = useState(externalSession || null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [cartRestored, setCartRestored]   = useState(false);
  const [orderRules, setOrderRules]   = useState(DEFAULT_ORDER_RULES);
  const [meetUpEvents, setMeetUpEvents] = useState([]);

  // ── Cart persistence ─────────────────────────────────────────────────────
  useEffect(() => {
    if (corporateClient) return;
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { savedStep, savedFormData } = JSON.parse(saved);
        if (savedFormData && Object.keys(savedFormData).length > 1) {
          setFormData(savedFormData);
          if (savedStep && !SPECIAL_STEPS.has(savedStep)) setStep(savedStep);
          setCartRestored(true);
        }
      }
    } catch (e) {
      console.warn('Could not restore cart:', e);
    }
  }, []);

  useEffect(() => {
    try {
      if (Object.keys(formData).length <= 1) return;
      if (SPECIAL_STEPS.has(step)) return; // don't persist special screens
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ savedStep: step, savedFormData: formData }));
    } catch (e) {
      console.warn('Could not save cart:', e);
    }
  }, [formData, step]);

  // ── Auth / profile ───────────────────────────────────────────────────────
  useEffect(() => {
    async function initSession() {
      if (!isSupabaseConfigured()) return;
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
        if (currentSession) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email, phone')
            .eq('id', currentSession.user.id)
            .single();
          if (profile) {
            setFormData(prev => ({
              ...prev,
              customer_name:  profile.full_name || prev.customer_name || '',
              customer_email: profile.email     || prev.customer_email || '',
              confirm_email:  profile.email     || prev.confirm_email || '',
              customer_phone: profile.phone     || prev.customer_phone || '',
            }));
          }
        }
      } catch (err) {
        console.error('Session/profile init failed:', err);
      } finally {
        setProfileLoaded(true);
      }
    }
    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      if (sess) {
        supabase
          .from('profiles')
          .select('full_name, email, phone')
          .eq('id', sess.user.id)
          .single()
          .then(({ data: profile }) => {
            if (profile) {
              setFormData(prev => ({
                ...prev,
                customer_name:  profile.full_name || prev.customer_name || '',
                customer_email: profile.email     || prev.customer_email || '',
                confirm_email:  profile.email     || prev.confirm_email || '',
                customer_phone: profile.phone     || prev.customer_phone || '',
              }));
            }
          });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Order rules ──────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchOrderRules() {
      if (!isSupabaseConfigured()) return;
      try {
        const { data } = await supabase
          .from('delivery_settings')
          .select([
            'meal_prep_delivery_days', 'meal_prep_cutoff_day', 'meal_prep_cutoff_time',
            'meal_prep_min_qty', 'meal_prep_max_qty', 'meal_prep_daily_limit',
            'corporate_delivery_days', 'corporate_cutoff_day', 'corporate_cutoff_time',
            'meet_up_min_qty', 'meet_up_discount_pct',
          ].join(','))
          .eq('id', 1)
          .single();
        if (data) {
          setOrderRules({
            meal_prep_delivery_days : data.meal_prep_delivery_days ?? DEFAULT_ORDER_RULES.meal_prep_delivery_days,
            meal_prep_cutoff_day    : data.meal_prep_cutoff_day    ?? DEFAULT_ORDER_RULES.meal_prep_cutoff_day,
            meal_prep_cutoff_time   : data.meal_prep_cutoff_time   ? data.meal_prep_cutoff_time.slice(0, 5) : DEFAULT_ORDER_RULES.meal_prep_cutoff_time,
            meal_prep_min_qty       : data.meal_prep_min_qty       ?? DEFAULT_ORDER_RULES.meal_prep_min_qty,
            meal_prep_max_qty       : data.meal_prep_max_qty       ?? DEFAULT_ORDER_RULES.meal_prep_max_qty,
            meal_prep_daily_limit   : data.meal_prep_daily_limit   ?? DEFAULT_ORDER_RULES.meal_prep_daily_limit,
            corporate_delivery_days : data.corporate_delivery_days ?? DEFAULT_ORDER_RULES.corporate_delivery_days,
            corporate_cutoff_day    : data.corporate_cutoff_day    ?? DEFAULT_ORDER_RULES.corporate_cutoff_day,
            corporate_cutoff_time   : data.corporate_cutoff_time   ? data.corporate_cutoff_time.slice(0, 5) : DEFAULT_ORDER_RULES.corporate_cutoff_time,
            meet_up_min_qty         : data.meet_up_min_qty         ?? DEFAULT_ORDER_RULES.meet_up_min_qty,
            meet_up_discount_pct    : data.meet_up_discount_pct    ?? DEFAULT_ORDER_RULES.meet_up_discount_pct,
          });
        }
      } catch (err) {
        console.error('Failed to fetch order rules:', err);
      }
    }
    fetchOrderRules();
  }, []);

  // ── Meet-up events ───────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchMeetUpEvents() {
      if (!isSupabaseConfigured()) return;
      try {
        const today = new Date().toISOString().split('T')[0];
        const { data } = await supabase
          .from('meet_up_events')
          .select('*')
          .eq('active', true)
          .gte('event_date', today)
          .order('event_date', { ascending: true });
        setMeetUpEvents(data || []);
      } catch (err) {
        console.error('Failed to fetch meet-up events:', err);
      }
    }
    fetchMeetUpEvents();
  }, []);

  // ── Corporate pre-fill ───────────────────────────────────────────────────
  useEffect(() => {
    if (!corporateClient) return;
    setFormData(prev => ({
      ...prev,
      delivery_address: prev.delivery_address || corporateClient.delivery_address || '',
      delivery_city:    prev.delivery_city    || corporateClient.delivery_city    || '',
      delivery_zip:     prev.delivery_zip     || corporateClient.delivery_zip     || '',
      delivery_location_type: prev.delivery_location_type || 'office',
      delivery_type: prev.delivery_type || 'scheduled',
      group_size: prev.group_size || 1,
    }));
  }, [corporateClient]);

  // ── Coordinator redirect ─────────────────────────────────────────────────
  useEffect(() => {
    if (corporateClient || !isSupabaseConfigured()) return;
    async function checkCoordinator() {
      const { data: { session: sess } } = await supabase.auth.getSession();
      if (!sess) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('corporate_client_id, corporate_clients(slug)')
        .eq('id', sess.user.id)
        .single();
      const slug = profile?.corporate_clients?.slug;
      if (slug) navigate(`/c/${slug}`, { replace: true });
    }
    checkCoordinator();
  }, [corporateClient]);

  // ── Scroll to top on step change ─────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const updateFormData = (newData) => setFormData(prev => ({ ...prev, ...newData }));

  const clearCart = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  };

  // ── Step 0 handler — routes based on order type ─────────────────────────
  const handleStep0Next = (type) => {
    if (type === 'corporate') { setStep('corporate_contact'); return; }
    if (type === 'catering')  { setStep('catering');          return; }
    // meal_prep and meet_up continue to the product picker
    setStep(1);
  };

  const isSpecialStep  = SPECIAL_STEPS.has(step);
  const displayStep    = getDisplayStep(step);
  const isMeetUp       = formData.delivery_type === 'meet_up';

  const handleStepClick = (s) => {
    if (s.display >= displayStep) return;
    setStep(s.stepKey);
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-16">

      {/* Cart restored banner */}
      {cartRestored && !isSpecialStep && (
        <div className="mb-6 max-w-lg mx-auto bg-blush-50 border border-blush-100 rounded-2xl px-5 py-3 flex items-center justify-between gap-3">
          <p className="text-sm text-sage-600 font-medium">
            Welcome back — your cart has been restored.
          </p>
          <button
            onClick={() => {
              clearCart();
              setFormData({ addons: [] });
              setStep(corporateClient ? 1 : '0');
              setCartRestored(false);
            }}
            className="text-xs text-blush-400 font-bold hover:text-blush-500 shrink-0"
          >
            Start over
          </button>
        </div>
      )}

      {/* Progress bar — hidden for special steps (corporate / catering) */}
      {!isSpecialStep && !corporateClient && (
        <div className="mb-12 flex justify-center items-center max-w-lg mx-auto">
          {DISPLAY_STEPS.map((s, i) => {
            const isCompleted = displayStep > s.display;
            const isCurrent   = displayStep === s.display;
            const isClickable = isCompleted;
            return (
              <React.Fragment key={s.display}>
                <div className="flex flex-col items-center gap-1.5 relative group">
                  <button
                    onClick={() => handleStepClick(s)}
                    disabled={!isClickable}
                    title={s.label}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                      isCurrent   ? 'bg-blush-300 text-white shadow-glow scale-110' :
                      isCompleted ? 'bg-blush-200 text-white hover:bg-blush-400 cursor-pointer hover:scale-110' :
                                    'bg-sage-100 text-sage-400 cursor-default'
                    }`}
                  >
                    {isCompleted ? '✓' : s.display}
                  </button>
                  {isClickable && (
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-bold text-blush-400 uppercase tracking-wider whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {s.label}
                    </span>
                  )}
                </div>
                {i < DISPLAY_STEPS.length - 1 && (
                  <div className={`h-1 flex-1 mx-2 rounded-full transition-colors duration-500 ${displayStep > s.display ? 'bg-blush-200' : 'bg-sage-100'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Step content */}
      <div className="relative max-w-3xl mx-auto">
        <AnimatePresence mode="wait">

          {/* ── Order type picker ── */}
          {step === '0' && (
            <Step0DeliveryType
              key="step0"
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleStep0Next}
              orderRules={orderRules}
              meetUpEvents={meetUpEvents}
            />
          )}

          {/* ── Special: Corporate contact screen ── */}
          {step === 'corporate_contact' && (
            <CorporateContactScreen
              key="corporate_contact"
              onBack={() => setStep('0')}
            />
          )}

          {/* ── Special: Catering inquiry form ── */}
          {step === 'catering' && (
            <CateringInquiryForm
              key="catering"
              onBack={() => setStep('0')}
            />
          )}

          {/* ── Product picker ── */}
          {step === 1 && (
            <Step1Products
              key="step1"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep('addons')}
              onBack={corporateClient ? undefined : () => setStep('0')}
              corporateClient={corporateClient}
              orderRules={orderRules}
            />
          )}

          {/* ── Add-ons ── */}
          {step === 'addons' && (
            <Step2Addons
              key="addons"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep(2)}
              onBack={() => setStep(1)}
            />
          )}

          {/* ── Delivery / Meet-Up details ── */}
          {step === 2 && (
            isMeetUp ? (
              <Step2MeetUp
                key="step2-meetup"
                formData={formData}
                updateFormData={updateFormData}
                onNext={() => setStep(corporateClient ? 4 : 3)}
                onBack={() => setStep('addons')}
                meetUpEvents={meetUpEvents}
              />
            ) : (
              <Step2Delivery
                key="step2-delivery"
                formData={formData}
                updateFormData={updateFormData}
                onNext={() => setStep(corporateClient ? 4 : 3)}
                onBack={() => setStep('addons')}
                orderRules={orderRules}
              />
            )
          )}

          {/* ── Customer info ── */}
          {step === 3 && !corporateClient && (
            <Step3Customer
              key="step3"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep(4)}
              onBack={() => setStep(2)}
              session={session}
              profileLoaded={profileLoaded}
            />
          )}

          {/* ── Review & payment ── */}
          {step === 4 && (
            <Step4Review
              key="step4"
              formData={formData}
              setStep={setStep}
              onBack={() => setStep(corporateClient ? 2 : 3)}
              session={session}
              onOrderComplete={clearCart}
              corporateClient={corporateClient}
              orderRules={orderRules}
            />
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
